import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowBack, Celebration, Extension, Refresh, Shuffle } from "@mui/icons-material";
import GameLeaderboardPanel from "../components/games/GameLeaderboardPanel";
import { divisionMappings } from "../Constants";
import { LogoPuzzleRoundView, startLogoPuzzleRun, submitLogoPuzzleSolve } from "../services/MinigameService";
import { fmtSolveTime } from "../components/games/gameBoards";
import styles from "./LogoPuzzlePage.module.css";

type Difficulty = "easy" | "hard";
type Phase = "loading" | "playing" | "solved";

const BEST_KEY = "logoPuzzleBest";

interface Best {
    moves: number;
    secs: number;
}

const loadBest = (): Record<Difficulty, Best | null> => {
    try {
        const raw = JSON.parse(localStorage.getItem(BEST_KEY) || "{}");
        return { easy: raw.easy ?? null, hard: raw.hard ?? null };
    } catch {
        return { easy: null, hard: null };
    }
};

const fmtTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
};

const LogoPuzzlePage: React.FC = () => {
    const { t } = useTranslation(["pages", "common"]);
    const [difficulty, setDifficulty] = useState<Difficulty>("easy");
    const [round, setRound] = useState<LogoPuzzleRoundView | null>(null);
    const [phase, setPhase] = useState<Phase>("loading");
    const [tiles, setTiles] = useState<number[]>([]);
    const [selected, setSelected] = useState<number | null>(null);
    const [moves, setMoves] = useState(0);
    const [elapsed, setElapsed] = useState(0);
    const [best, setBest] = useState<Record<Difficulty, Best | null>>(loadBest);
    // The server's time for the solve, once it has checked the moves.
    const [officialMs, setOfficialMs] = useState<number | null>(null);
    // Bumped when a solve lands, so the leaderboard panel refetches.
    const [boardKey, setBoardKey] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // Each puzzle is its own run; every swap is kept so the server can replay the solve.
    const runId = useRef<string | null>(null);
    const swaps = useRef<[number, number][]>([]);
    // Each load gets a number; a response for an older one (a quick difficulty/mode toggle) is ignored.
    const loadSeq = useRef(0);
    const [loadFailed, setLoadFailed] = useState(false);

    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    // Deal a puzzle: a new crest, or (retryOf) a fresh scramble of the current one.
    const deal = useCallback(async (retryOf?: string) => {
        stopTimer();
        setPhase("loading");
        setSelected(null);
        setMoves(0);
        setElapsed(0);
        setOfficialMs(null);
        setLoadFailed(false);
        swaps.current = [];
        const seq = ++loadSeq.current;
        const view = await startLogoPuzzleRun(difficulty, retryOf);
        if (seq !== loadSeq.current) return;
        if (!view) {
            setLoadFailed(true);
            return;
        }
        runId.current = view.runId;
        setRound(view.round);
        setTiles(view.round.tiles);
        setPhase("playing");
        timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    }, [difficulty]);

    const loadRound = useCallback(() => deal(), [deal]);

    // New puzzle whenever the difficulty changes (and on first mount).
    useEffect(() => {
        loadRound();
        return stopTimer;
    }, [loadRound]);

    // "Bland igen": rescramble the current crest and restart the counters.
    const reshuffle = () => {
        if (!round || phase !== "playing" || !runId.current) return;
        deal(runId.current);
    };

    const formatDivision = (d: string) => divisionMappings.get(d) || d;

    const recordBest = (finalMoves: number, finalSecs: number) => {
        setBest((prev) => {
            const cur = prev[difficulty];
            const better = !cur || finalMoves < cur.moves || (finalMoves === cur.moves && finalSecs < cur.secs);
            if (!better) return prev;
            const next = { ...prev, [difficulty]: { moves: finalMoves, secs: finalSecs } };
            localStorage.setItem(BEST_KEY, JSON.stringify(next));
            return next;
        });
    };

    const submitSolve = async (finalMoves: number, finalSecs: number) => {
        recordBest(finalMoves, finalSecs);
        const id = runId.current;
        if (!id) return;
        const res = await submitLogoPuzzleSolve(id, swaps.current);
        if (res?.roundWon && runId.current === id) {
            setOfficialMs(res.elapsedMs);
            setBoardKey((k) => k + 1);
        }
    };

    const handleTileClick = (pos: number) => {
        if (phase !== "playing") return;
        if (selected === null) {
            setSelected(pos);
            return;
        }
        if (selected === pos) {
            setSelected(null);
            return;
        }
        const next = tiles.slice();
        [next[selected], next[pos]] = [next[pos], next[selected]];
        swaps.current.push([selected, pos]);
        setSelected(null);
        setTiles(next);
        const newMoves = moves + 1;
        setMoves(newMoves);
        if (next.every((v, i) => v === i)) {
            stopTimer();
            setPhase("solved");
            submitSolve(newMoves, elapsed);
        }
    };

    const n = round?.grid ?? 3;
    const b = best[difficulty];
    const solved = phase === "solved";

    // Background-position for the sub-square that piece `p` shows, in an n×n sprite.
    const pieceStyle = (p: number): React.CSSProperties => {
        const col = p % n;
        const row = Math.floor(p / n);
        return {
            backgroundImage: round ? `url(${round.logoUrl})` : undefined,
            backgroundSize: `${n * 100}% ${n * 100}%`,
            backgroundPosition: `${(col * 100) / (n - 1)}% ${(row * 100) / (n - 1)}%`
        };
    };

    const promptText = solved
        ? t('logoPuzzle.promptSolved')
        : selected === null
            ? t('logoPuzzle.promptPickTwo')
            : t('logoPuzzle.promptPickMore');

    return (
        <div className={styles.page}>
            <Link to="/games" className={styles.backLink}>
                <ArrowBack sx={{ fontSize: 18 }} />
                {t('logoPuzzle.back')}
            </Link>

            {/* ===== Header ===== */}
            <div className={styles.header}>
                <div className={styles.identity}>
                    <span className={styles.gameIcon}>
                        <Extension sx={{ fontSize: 28 }} />
                    </span>
                    <div>
                        <h1 className={styles.title}>Logo Puzzle</h1>
                        <p className={styles.tagline}>{t('logoPuzzle.tagline')}</p>
                    </div>
                </div>
                <div className={styles.toggleGroup}>
                    <button
                        className={`${styles.toggleBtn} ${difficulty === "easy" ? styles.toggleBtnActive : ""}`}
                        onClick={() => setDifficulty("easy")}
                    >
                        {t('logoPuzzle.easy')}
                    </button>
                    <button
                        className={`${styles.toggleBtn} ${difficulty === "hard" ? styles.toggleBtnActive : ""}`}
                        onClick={() => setDifficulty("hard")}
                    >
                        {t('logoPuzzle.hard')}
                    </button>
                </div>
            </div>

            {/* ===== Scoreboard ===== */}
            <div className={styles.scoreboard}>
                <div className={styles.scoreTile}>
                    <div className={styles.scoreLabel}>{t('logoPuzzle.moves')}</div>
                    <div className={styles.scoreValue}>{moves}</div>
                </div>
                <div className={styles.scoreTile}>
                    <div className={styles.scoreLabel}>{t('logoPuzzle.time')}</div>
                    <div className={styles.scoreValue}>{fmtTime(elapsed)}</div>
                </div>
                <div className={styles.scoreTile}>
                    <div className={styles.scoreLabel}>{t('logoPuzzle.best')}</div>
                    <div className={`${styles.scoreValue} ${styles.scoreValueBest}`}>{b ? `${b.moves}` : "—"}</div>
                </div>
            </div>

            {phase === "loading" && (
                <div className={styles.loading}>
                    {loadFailed
                        ? <>{t('gamesLeaderboard.loadFailed')} <button className={styles.nextBtn} onClick={() => loadRound()}>{t('gamesLeaderboard.retry')}</button></>
                        : t('logoPuzzle.loading')}
                </div>
            )}

            {round && phase !== "loading" && (
                <>
                    <div className={`${styles.prompt} ${solved ? styles.promptSolved : ''}`}>{promptText}</div>

                    <div
                        className={`${styles.board} ${n === 4 ? styles.boardHard : ''} ${solved ? styles.boardSolved : ""}`}
                        style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}
                    >
                        {tiles.map((piece, pos) => (
                            <div
                                key={pos}
                                className={`${styles.tile} ${selected === pos ? styles.tileSelected : ""} ${solved ? styles.tileDone : ""}`}
                                style={pieceStyle(piece)}
                                onClick={() => handleTileClick(pos)}
                            />
                        ))}
                    </div>

                    <div className={styles.actions}>
                        {solved ? (
                            <div className={styles.solvedCard}>
                                <div className={styles.solvedMsg}>
                                    <Celebration sx={{ fontSize: 22 }} />
                                    {t('logoPuzzle.solved', { team: round.teamName })}
                                </div>
                                <div className={styles.solvedStats}>
                                    {t('logoPuzzle.solvedStats', { moves, time: officialMs != null ? fmtSolveTime(officialMs) : fmtTime(elapsed) })}
                                </div>
                                <div className={styles.solvedContext}>
                                    {t('common:seasonNum', { number: round.season })} · {formatDivision(round.division)}
                                </div>
                                <div>
                                    <button className={styles.nextBtn} onClick={loadRound}>
                                        <Refresh sx={{ fontSize: 19 }} />
                                        {t('logoPuzzle.nextLogo')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button className={styles.shuffleBtn} onClick={reshuffle}>
                                <Shuffle sx={{ fontSize: 18 }} />
                                {t('logoPuzzle.reshuffle')}
                            </button>
                        )}
                    </div>
                </>
            )}

            <GameLeaderboardPanel game="logo-puzzle" variant={difficulty} refreshKey={boardKey} />
        </div>
    );
};

export default LogoPuzzlePage;
